import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import { Week52HighLowResponse } from '../../../../../lib/models/fundamental/types';
import { RootState } from '../../../../../lib/store';
import { INVALID_VALUE } from '../../../../../lib/util/types';

import removeIcon from '../../../../../assets/img/modify.svg';
import { ChartLine } from '../../../../../shared_components/ChartLine';
import ClickableDiv from '../../../../../shared_components/ClickableDiv/ClickableDiv';
import ClickableImage from '../../../../ClickableImage/ClickableImage';
import CompanyInfoBlock from '../../../../CompanyInfoBlock/CompanyInfoBlock';
import { getColumnCountFromGrid } from '../../../../WatchList/WatchList.constans';
import { BaseRowProps, VariantOneProps } from '../../SymbolTableRow.types';

import styles from './SymbolTableRowDesktop.module.scss';

type OwnProps = BaseRowProps & {
  model: VariantOneProps;
};

type Props = OwnProps & ReduxProps;

const gridClassMap: Record<string, string> = {
  FIVE_ROW_ITEMS_GRID: styles['rowContent--fiveRowItemsGrid'],
  FOUR_ROW_ITEMS_GRID: styles['rowContent--fourRowItemsGrid'],
};

const SymbolTableRowDesktop = React.memo(({
  model,
  hasSwipeableRows,
  onRemove,
  onRowClick,
  name,
  oneRowInfoContent,
  gridTemplateColumns,
  companyLogoAndName,
  isWeek52Item,
  isWithNews,
}: Props) => {
  const {
    stock,
    chartSymbol,
    priceMovement,
    direction,
    changePrice,
    lastPrice,
    changePercent,
    onToggleShowPercent,
  } = model;

  const { symbol } = stock;
  const symbolForChart = chartSymbol || symbol;

  const { url, logo } = companyLogoAndName[symbol] ?? {};

  let resolvedChangePercent: string;
  if (isWeek52Item) {
    const sign = direction === '-' ? '-' : '+';
    resolvedChangePercent = `${sign}${Math.abs((stock as Week52HighLowResponse).changePercent).toFixed(2)}%`;
  } else {
    resolvedChangePercent = changePercent ?? INVALID_VALUE;
  }

  const columnCount = getColumnCountFromGrid(gridTemplateColumns);

  return (
    <div className={styles.row}>
      <ClickableDiv
        className={classNames(
          styles.rowContent,
          gridTemplateColumns && gridClassMap[gridTemplateColumns],
        )}
        onClick={onRowClick}
      >
        <CompanyInfoBlock
          item={stock}
          itemUrl={url || ''}
          companyName={name}
          logo={logo}
          oneRowInfoContent={oneRowInfoContent}
          columnCount={columnCount}
          isWithNews={isWithNews}
        />

        <div className={styles.columnChart}>
          <ChartLine symbol={symbolForChart} />
        </div>

        {lastPrice && (
          <div className={styles.columnLastPrice}>
            <div className={styles.currentOrAskPriceWrapper}>
              <span
                className={classNames(styles.currentOrAskPrice, {
                  [styles.noPriceChange]: priceMovement === 'equal',
                  [styles.negativeChange]: priceMovement === 'down',
                  [styles.positiveChange]: priceMovement === 'up',
                })}
              >
                {lastPrice}
              </span>
            </div>
          </div>
        )}

        {changePrice && (
          <div className={styles.columnChange}>
            <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
              {changePrice}
            </span>
          </div>
        )}

        {changePercent && (
          <div className={styles.columnPercentChange}>
            <div className={styles.percentageAndButtonWrapper}>
              <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                {resolvedChangePercent}
              </span>
            </div>
          </div>
        )}
      </ClickableDiv>
      {hasSwipeableRows && (
        <ClickableImage
          src={removeIcon}
          className={styles.removeButton}
          onClick={onRemove}
        />
      )}
    </div>
  );
});

const mapStateToProps = (state: RootState) => ({
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SymbolTableRowDesktop);
